import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { projectsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

// Copied from the main app's DeleteProjectModal, plus a typed-name confirmation (kickoff
// instructions above): deleting a project also soft-deletes every video and run inside it
// (backend/app/api/v1/projects.py DELETE), so a single misclick is unrecoverable through the
// UI - the confirmation input guards against that.
export default function DeleteProjectModal({ project, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const queryClient = useQueryClient();

  const deleteProjectMutation = useMutation(() => projectsAPI.remove(project.id), {
    onSuccess: () => {
      toast.success('Project deleted');
      queryClient.invalidateQueries(['projects']);
      queryClient.removeQueries(['project', project.id]);
      onDeleted?.();
      onClose?.();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to delete project'));
    },
  });

  if (!project) return null;

  const isConfirmed = confirmText === project.name;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" aria-hidden="true" />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div
          className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          role="dialog"
          data-testid="project-delete-dialog"
          aria-modal="true"
          aria-labelledby="delete-project-modal-title"
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-red-100">
              <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 id="delete-project-modal-title" className="text-lg leading-6 font-medium text-gray-900">
                Delete Project
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <strong>{project.name}</strong>? This will permanently remove
                  the project and every video, run, and finding inside it. This action cannot be undone.
                </p>
              </div>
              <div className="mt-4 text-left">
                <label htmlFor="delete-confirm-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Type <strong>{project.name}</strong> to confirm
                </label>
                <input
                  id="delete-confirm-name"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={project.name}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  disabled={deleteProjectMutation.isLoading}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              data-testid="project-delete-confirm"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => deleteProjectMutation.mutate()}
              disabled={!isConfirmed || deleteProjectMutation.isLoading}
            >
              {deleteProjectMutation.isLoading ? 'Deleting…' : 'Delete'}
            </button>
            <button
              type="button"
              data-testid="project-delete-cancel"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={deleteProjectMutation.isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
